import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  alpha,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import { Send, CheckCircle } from '@mui/icons-material';
import { createDemoRequest } from '../services/demoRequest';

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    bgcolor: '#FFFFFF',
    color: '#0F172A',
    '& fieldset': { borderColor: '#CBD5E1' },
    '&:hover fieldset': { borderColor: '#3B82F6' },
    '&.Mui-focused fieldset': { borderColor: '#3B82F6' },
  },
  '& .MuiOutlinedInput-input': {
    color: '#0F172A',
    '&::placeholder': { color: '#94A3B8', opacity: 1 },
  },
  '& .MuiInputLabel-root': { color: '#475569' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#3B82F6' },
} as const;

const RequestDemoForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    institute: '',
    email: '',
    contactNumber: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await createDemoRequest(formData);
      setSuccess(true);
      setFormData({
        name: '',
        institute: '',
        email: '',
        contactNumber: '',
      });
      setOpenSnackbar(true);
    } catch (err: any) {
      setError(err.message || 'Failed to submit demo request. Please try again.');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  if (success) {
    return (
      <Box
        id="demo-form"
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(to bottom, #FFFFFF, #F0F9FF)',
        }}
      >
        <Container maxWidth="md">
          <Box
            sx={{
              textAlign: 'center',
              p: { xs: 4, md: 6 },
              bgcolor: 'white',
              borderRadius: 4,
              boxShadow: `0 20px 60px ${alpha('#3B82F6', 0.15)}`,
              border: `1px solid ${alpha('#3B82F6', 0.1)}`,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha('#10B981', 0.1),
                mb: 3,
              }}
            >
              <CheckCircle sx={{ fontSize: 64, color: '#10B981' }} />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#1E293B',
                mb: 2,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
              }}
            >
              Request Submitted Successfully!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#64748B',
                fontSize: { xs: '1rem', md: '1.125rem' },
                mb: 3,
                lineHeight: 1.7,
              }}
            >
              Thank you for your interest in EduBook! We've received your demo request
              and our team will get in touch with you shortly to schedule a personalized
              demonstration.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => setSuccess(false)}
              sx={{
                mt: 2,
                color: '#3B82F6',
                borderColor: '#3B82F6',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '50px',
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderColor: '#2563EB',
                  bgcolor: alpha('#3B82F6', 0.05),
                },
              }}
            >
              Submit Another Request
            </Button>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      id="demo-form"
      sx={{
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(to bottom, #FFFFFF, #F0F9FF)',
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: '#1E293B',
              fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
              mb: 2,
            }}
          >
            Request a{' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(to right, #3B82F6, #2DD4BF)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Free Demo
            </Box>
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: '#64748B',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              maxWidth: '600px',
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            Experience the power of EduBook firsthand. Fill out the form below and we'll
            schedule a personalized demonstration tailored to your institution's needs.
          </Typography>
        </Box>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: { xs: 3, md: 5 },
            bgcolor: 'white',
            borderRadius: 4,
            boxShadow: `0 20px 60px ${alpha('#3B82F6', 0.15)}`,
            border: `1px solid ${alpha('#3B82F6', 0.1)}`,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              required
              name="name"
              label="Full Name"
              placeholder="John Doe"
              value={formData.name}
              onChange={handleChange}
              disabled={loading}
              sx={fieldSx}
            />

            <TextField
              fullWidth
              required
              name="institute"
              label="Institute Name"
              placeholder="ABC School of Excellence"
              value={formData.institute}
              onChange={handleChange}
              disabled={loading}
              sx={fieldSx}
            />

            <TextField
              fullWidth
              required
              name="email"
              type="email"
              label="Email Address"
              placeholder="john.doe@example.com"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              sx={fieldSx}
            />

            <TextField
              fullWidth
              required
              name="contactNumber"
              label="Contact Number"
              placeholder="+1 (555) 123-4567"
              value={formData.contactNumber}
              onChange={handleChange}
              disabled={loading}
              sx={fieldSx}
            />

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                mt: 2,
                height: '56px',
                bgcolor: '#3B82F6',
                color: 'white',
                fontSize: '1.125rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '50px',
                boxShadow: `0 10px 25px ${alpha('#3B82F6', 0.3)}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&:hover': {
                  bgcolor: '#2563EB',
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 30px ${alpha('#3B82F6', 0.4)}`,
                },
                '&:disabled': {
                  bgcolor: alpha('#3B82F6', 0.5),
                  color: 'white',
                },
                transition: 'all 0.3s',
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                <>
                  Submit Request
                  <Send sx={{ fontSize: 20 }} />
                </>
              )}
            </Button>
          </Box>

          <Typography
            variant="body2"
            sx={{
              mt: 3,
              textAlign: 'center',
              color: '#64748B',
              fontSize: '0.875rem',
            }}
          >
            By submitting this form, you agree to receive communications from EduBook
            regarding your demo request.
          </Typography>
        </Box>
      </Container>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {error || 'Demo request submitted successfully!'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RequestDemoForm;

